import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, ReplaySubject } from 'rxjs';
import { Subject, interval } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { Network } from '@capacitor/network';

import { AlertController } from '@ionic/angular';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { AlertService } from '@core/services/alert/alert.service';
import { ApiUrlService } from '@core/services/api/api-url.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { ApplicationStatus } from '@core/services/online-credit/online-credit.model';
import { OnlineCreditService } from '@core/services/online-credit/online-credit.service';
import { StepManagerService } from '@core/services/online-credit/step-manager.service';

import { LifecycleHookLogs } from '@shared/decorators/lifecycle-hook-logs/lifecycle-hook-logs.decorator';
import { NetworkService } from '@shared/services/network.service';

import { Steps } from '../models/step.model';
import { StoreService } from '../store/store.service';

@LifecycleHookLogs()
@UntilDestroy()
@Component({
    selector: 'app-account-confirmation-step',
    templateUrl: 'account-confirmation-step.page.html',
    styleUrls: ['./account-confirmation-step.page.scss', '../new-credit.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AccountConfirmationStepPage implements OnInit, OnDestroy {
  title: string = 'Подтверждение учетной записи';
  subtitle: string = '';

  public loaded$: Observable<boolean>;

  isDisabled: boolean = false;
  private esiaStatusChanged$ = new ReplaySubject(1);

  constructor(
    private stepManagerService: StepManagerService,
    private onlineCreditService: OnlineCreditService,
    private router: Router,
    private apiUrl: ApiUrlService,
    private store: StoreService,
    private alertService: AlertService,
    private alertCtrl: AlertController,
    private navigationService: NavigationService,
    private networkService: NetworkService
  ) {
    this.loaded$ = this.stepManagerService.stepLoaded$;
  }

  ngOnInit() {
    if (this.store.applicationStatus === ApplicationStatus.PublicServices) {
      this.awaitChangeEsiaStatus();
    } else {
      setTimeout(() => {
        this.stepManagerService.setStepLoaded(true);
      });
    }
  }

  ionViewWillEnter() {}

  ngOnDestroy() {
    this.setEsiaStatusChanged();
  }

  ionViewWillLeave() {}

  awaitChangeEsiaStatus() {
    const esiaConfirmedStatus$ = this.onlineCreditService
      .getApplicationData()
      .pipe(
        filter((application) =>
          [ApplicationStatus.ConfirmPublicServices, ApplicationStatus.ChoiceOffice].includes(application.status)
        )
      );
    this.stepManagerService.loaderTitle = 'Обработка запроса, пожалуйста подождите';

    interval(5000)
      .pipe(
        switchMap(() => esiaConfirmedStatus$),
        takeUntil(this.esiaStatusChanged$)
      )
      .subscribe(
        (application) => {
          this.setEsiaStatusChanged();
          this.store.updateApplication(application);
          if (application.esiaReason !== '0') {
            this.stepManagerService.setStepLoaded(true);
          } else {
            const nextStep =
              application.status === ApplicationStatus.ConfirmPublicServices
                ? Steps.signAgreements
                : Steps.accountConfirmation;
            void this.navigationService.push('/new-product/credit/' + nextStep, { replaceUrl: true });
            // this.stepManagerService.changeStep(nextStep);
          }
        },
        (error) => {
          this.setEsiaStatusChanged();
          this.stepManagerService.showErrorStepAlert(error);
        }
      );
  }

  setEsiaStatusChanged() {
    this.esiaStatusChanged$.next(true);
    this.esiaStatusChanged$.complete();
    this.esiaStatusChanged$ = new ReplaySubject(1);
    this.isDisabled = false;
  }

  confirmAccount() {
    // this.sendConsentRequest();
    this.sendEsiaRequest();
  }

  registerAccount() {
    // this.sendConsentRequest();
    this.sendEsiaRequest();
  }

  async sendEsiaRequest() {
    await this.networkService.getStatus().then((status) => {
      if (!status) {
        this.alertService.showInfoAlert('Отсутствует подключение к интернету!');
        return;
      }
      this.stepManagerService.setStepLoaded(false);
      this.isDisabled = true;
      this.onlineCreditService
        .sendApplicationData({
          id: this.store.value.application.id,
          gosuslugiRequest: true
        })
        .pipe(untilDestroyed(this))
        .subscribe(
          (res) => {
            this.awaitChangeEsiaStatus();
            this.isDisabled = false;
          },
          (error) => {
            // this.stepManagerService.showErrorStepAlert(err.message || err.description || 'Неизвестная ошибка!');
            const message = error.message || error.description || error || 'Неизвестная ошибка!';
            this.alertService.showInfoAlert(message);
            this.stepManagerService.setStepLoaded(true);
            this.isDisabled = false;
          }
        );
    });
  }

  private sendConsentRequest() {
    //TESTONLINE ONLY
    const applicationId = this.store.value.application.id;
    this.onlineCreditService.createOnlineCreditConsent(applicationId).subscribe(
      (res) => {
        if (res && res.creditBureauConsents && res.pensionFundConsents) {
          this.store.updateApplication(res);
          this.sendEsiaRequest();
        } else {
          this.stepManagerService.showErrorStepAlert('Неизвестная ошибки');
        }
      },
      (err) => {
        this.stepManagerService.showErrorStepAlert(err);
      }
    );
  }

  goBack() {
    this.stepManagerService.setStepLoaded(false);
    void this.navigationService.push('/new-product/credit/choice-office', { replaceUrl: true });
    // this.stepManagerService.changeStep(Steps.choiceOffice);
  }

  abort() {
    this.stepManagerService.showAbortAlert();
  }
}
